import { useTranslation } from "react-i18next";
import { enumMembers, type ApiSchema } from "../../../api/apiSchema";
import { ANNOTATION_DESTINATIONS, type AgentStepDefinition } from "../../../api/content/authoring/stepDecoders";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { itemsOfKind, type AuthoringOptions } from "../draft/authoringOptions";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import Section from "../../../ui/forms/Section";
import RequirementsEditor from "./RequirementsEditor";
import { withDmwTool } from "./stepDefinitions";
import { NESTED_OPTIONS_CLASS } from "../shared/authoringStyles";
import OptionLabel from "../../../ui/forms/OptionLabel";
import FieldGroup from "../../../ui/forms/FieldGroup";
import Field from "../../../ui/forms/Field";
import EmptyState from "../../../ui/feedback/EmptyState";
import { itemKindText } from "../../workflows/inputCards/typeWords";

/** Item kinds a change-set output may hold: the API's own enum. */
const ITEM_KINDS = enumMembers<ApiSchema<"ProjectItemKind">>({
  ontology_module: true,
  shapes: true,
  competency_question: true,
  few_shot_example: true,
  prompt: true,
  label_list: true,
  step: true,
  workflow: true,
  tool: true,
  skill: true,
  artifact: true,
  capability_overrides: true,
});

interface AgentStepBehaviourProps {
  step: AgentStepDefinition;
  /** The prompt's placeholders, for the annotations output's label list. */
  names: readonly string[];
  options: AuthoringOptions;
  check: DefinitionCheck;
  onChange: (step: AgentStepDefinition) => void;
}

/** What an agent step may use (tools, skills) and must deliver (output), and how it runs. */
export default function AgentStepBehaviour({ step, names, options, check, onChange }: AgentStepBehaviourProps) {
  const { t } = useTranslation();
  const catalogue = options.catalogue.status === "loaded" ? options.catalogue.value : null;
  const dmwTools = [...new Set([...(catalogue?.dmwTools.map((tool) => tool.name) ?? []), ...step.tools.dmw])].sort();
  const itemTools = itemsOfKind(options, "tool");
  const skills = itemsOfKind(options, "skill");
  const compactions = catalogue?.compactionMethods ?? [{ method: step.context_compaction.method, version: step.context_compaction.version, description: "" }];

  return (
    <>
      <Section title={t("authoring.step.agent.tools")} hint={t("authoring.step.agent.toolsHint")}>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {dmwTools.map((tool) => (
            <OptionLabel key={tool} size="small">
              <input type="checkbox" checked={step.tools.dmw.includes(tool)} onChange={(event) => { onChange(withDmwTool(step, tool, event.target.checked)); }} />
              {tool}
            </OptionLabel>
          ))}
        </div>
        {itemTools.length === 0 ? null : (
          <FieldGroup legend={t("authoring.step.agent.itemTools")} className="space-y-1">
            {itemTools.map((tool) => (
              <OptionLabel key={tool.reference}>
                <input
                  type="checkbox"
                  checked={step.tools.item_tools.includes(tool.reference)}
                  onChange={(event) => {
                    const others = step.tools.item_tools.filter((reference) => reference !== tool.reference);
                    onChange({ ...step, tools: { ...step.tools, item_tools: event.target.checked ? [...others, tool.reference] : others } });
                  }}
                />
                {tool.name}
              </OptionLabel>
            ))}
          </FieldGroup>
        )}
        <FieldProblems check={check} path="tools" />
      </Section>
      <Section title={t("authoring.step.agent.skills")} hint={t("authoring.step.agent.skillsHint")}>
        {skills.length === 0 ? <EmptyState size="small">{t("authoring.step.agent.noSkills")}</EmptyState> : null}
        <ul className="space-y-1">
          {skills.map((skill) => {
            const use = step.skills.find((candidate) => candidate.skill.split("@")[0] === skill.itemId);
            return (
              <li key={skill.itemId} className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1">{skill.name}</span>
                <select
                  className={`${FORM_SELECT_CLASS} w-auto text-xs`}
                  aria-label={t("authoring.step.agent.skillUse", { name: skill.name })}
                  value={use?.use ?? "none"}
                  onChange={(event) => {
                    const others = step.skills.filter((candidate) => candidate !== use);
                    const value = event.target.value;
                    onChange({ ...step, skills: value === "offered" || value === "requested" ? [...others, { skill: use?.skill ?? skill.reference, use: value }] : others });
                  }}
                >
                  <option value="none">{t("authoring.step.agent.skillUses.none")}</option>
                  <option value="offered">{t("authoring.step.agent.skillUses.offered")}</option>
                  <option value="requested">{t("authoring.step.agent.skillUses.requested")}</option>
                </select>
              </li>
            );
          })}
        </ul>
        <FieldProblems check={check} path="skills" />
      </Section>
      <OutputFields step={step} names={names} onChange={onChange} />
      <Section title={t("authoring.step.agent.running")}>
        <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={t("authoring.step.agent.session")}>
          {(["isolated", "thread"] as const).map((mode) => (
            <OptionLabel key={mode}>
              <input type="radio" checked={step.session.mode === mode} onChange={() => { onChange({ ...step, session: { mode } }); }} />
              {t(`authoring.step.agent.sessions.${mode}`)}
            </OptionLabel>
          ))}
        </div>
        <Field layout="inline" label={t("authoring.step.agent.compaction")}>
          {(control) => (
            <select
              {...control}
              className={`${FORM_SELECT_CLASS} w-auto`}
              value={`${step.context_compaction.method}@${String(step.context_compaction.version)}`}
              onChange={(event) => {
                const chosen = compactions.find((method) => `${method.method}@${String(method.version)}` === event.target.value);
                if (chosen !== undefined) {
                  const parameters = chosen.method === step.context_compaction.method ? step.context_compaction.parameters : {};
                  onChange({ ...step, context_compaction: { method: chosen.method, version: chosen.version, parameters } });
                }
              }}
            >
              {compactions.map((method) => <option key={`${method.method}@${String(method.version)}`} value={`${method.method}@${String(method.version)}`} title={method.description}>{method.method} @{method.version}</option>)}
            </select>
          )}
        </Field>
        <FieldProblems check={check} path="context_compaction" />
        <RequirementsEditor requirements={step.requirements} onChange={(requirements) => { onChange({ ...step, requirements }); }} />
        <FieldProblems check={check} path="requirements" />
      </Section>
    </>
  );
}

function OutputFields({ step, names, onChange }: { step: AgentStepDefinition; names: readonly string[]; onChange: (step: AgentStepDefinition) => void }) {
  const { t } = useTranslation();
  const output = step.output;
  const choose = (kind: ApiSchema<"OutputContract">["kind"]) => {
    if (kind === "text") {
      onChange({ ...step, output: { kind } });
    } else if (kind === "annotations") {
      onChange({ ...step, output: { kind, label_list_placeholder: names[0] ?? "", required: true, destination: "content_change_set" } });
    } else {
      onChange({ ...step, output: { kind, allowed_item_kinds: ["ontology_module"], required: true } });
    }
  };
  return (
    <Section title={t("authoring.step.agent.output")} hint={t("authoring.step.agent.outputHint")}>
      <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={t("authoring.step.agent.output")}>
        {(["text", "annotations", "change_set"] as const).map((kind) => (
          <OptionLabel key={kind}>
            <input type="radio" checked={output.kind === kind} onChange={() => { choose(kind); }} />
            {t(`authoring.step.agent.outputs.${kind}`)}
          </OptionLabel>
        ))}
      </div>
      {output.kind === "annotations" ? (
        <div className={NESTED_OPTIONS_CLASS}>
          <Field layout="inline" label={t("authoring.step.agent.labelListIn")}>
            {(control) => (
              <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={output.label_list_placeholder} onChange={(event) => { onChange({ ...step, output: { ...output, label_list_placeholder: event.target.value } }); }}>
                {names.includes(output.label_list_placeholder) ? null : <option value={output.label_list_placeholder}>{output.label_list_placeholder || "—"}</option>}
                {names.map((name) => <option key={name} value={name}>{`{${name}}`}</option>)}
              </select>
            )}
          </Field>
          {ANNOTATION_DESTINATIONS.map((destination) => (
            <OptionLabel key={destination}>
              <input type="radio" checked={output.destination === destination} onChange={() => { onChange({ ...step, output: { ...output, destination } }); }} />
              {t(`authoring.step.agent.destinations.${destination}`)}
            </OptionLabel>
          ))}
        </div>
      ) : null}
      {output.kind === "change_set" ? (
        <FieldGroup legend={t("authoring.step.agent.allowedKinds")} legendHidden className="flex flex-wrap gap-x-4 gap-y-1 border-l-2 pl-3 text-xs">
          {ITEM_KINDS.map((kind) => (
            <OptionLabel key={kind}>
              <input
                type="checkbox"
                checked={output.allowed_item_kinds.includes(kind)}
                onChange={(event) => {
                  const next = event.target.checked ? [...output.allowed_item_kinds, kind] : output.allowed_item_kinds.filter((allowed) => allowed !== kind);
                  if (next.length > 0) {
                    onChange({ ...step, output: { ...output, allowed_item_kinds: next } });
                  }
                }}
              />
              {itemKindText(kind, t)}
            </OptionLabel>
          ))}
        </FieldGroup>
      ) : null}
      {output.kind === "text" ? null : (
        <OptionLabel>
          <input type="checkbox" checked={output.required} onChange={(event) => { onChange({ ...step, output: { ...output, required: event.target.checked } }); }} />
          {t("authoring.step.agent.outputRequired")}
        </OptionLabel>
      )}
    </Section>
  );
}
